import { CORPUS_LIST_DEFAULT_LIMIT, CorpusEntry, type CorpusStatus, type PageMeta } from '@rp/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useReadySession } from '../../session/useSession'

export const CORPUS_PAGE_SIZE = CORPUS_LIST_DEFAULT_LIMIT

export const corpusKeys = {
  all: ['corpus'] as const,
  entries: (tenantId: string, status: CorpusStatus, page: number) =>
    ['corpus', 'entries', tenantId, status, page] as const,
}

export type CorpusEntryPage = { entries: readonly CorpusEntry[]; meta: PageMeta }

export const useCorpusEntries = (status: CorpusStatus, page: number) => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: corpusKeys.entries(membership.tenantId, status, page),
    queryFn: async (): Promise<CorpusEntryPage> => {
      const result = await api.request(z.array(CorpusEntry), {
        method: 'GET',
        path: '/v1/corpus/entries',
        query: { status, page, limit: CORPUS_PAGE_SIZE },
      })
      return { entries: result.data, meta: result.meta ?? { total: result.data.length, page, limit: CORPUS_PAGE_SIZE } }
    },
  })
}

export const useRetireCorpusEntry = () => {
  const { api } = useReadySession()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.request(CorpusEntry, { method: 'POST', path: `/v1/corpus/entries/${id}/retire` })).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: corpusKeys.all }),
  })
}
